/**
 * Sentry Error Monitoring — GDPR-Compliant Configuration
 *
 * MANDATORY settings (see CONSTRAINTS.md):
 *   - maskAllText: true    → masks participant names, emails in replays
 *   - blockAllMedia: true  → prevents participant video capture in replays
 *   - sendDefaultPii: NOT used → Dekko handles participant data
 *
 * DO NOT change maskAllText or blockAllMedia without a formal legal/privacy review.
 */

import * as Sentry from '@sentry/react';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

export const initSentry = () => {
    if (!SENTRY_DSN) {
        console.warn('[Sentry] VITE_SENTRY_DSN not set — error monitoring disabled');
        return;
    }

    Sentry.init({
        dsn: SENTRY_DSN,

        // Environment detection
        environment: import.meta.env.MODE, // 'development' | 'production'

        // Performance: 10% in production, 100% in development
        tracesSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 1.0,

        // Session Replay — GDPR mandatory settings
        replaysSessionSampleRate: 0.1, // 10% of normal sessions
        replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration({
                // MANDATORY — GDPR: masks participant names, emails in replays
                maskAllText: true,
                // MANDATORY — GDPR: prevents participant video capture in replays
                blockAllMedia: true,
            }),
        ],

        // Filter out known non-actionable errors
        beforeSend(event) {
            // Ignore ResizeObserver loop errors (browser noise, not actionable)
            if (event.exception?.values?.[0]?.value?.includes('ResizeObserver loop')) {
                return null;
            }
            return event;
        },
    });

    console.log(`[Sentry] Initialized (env: ${import.meta.env.MODE})`);
};

// Re-export Sentry for use in error handlers (e.g., firestoreErrorMonitor)
export { Sentry };
